const ERROR_SELECTOR = ".vc-input-details--error";
const FOCUSABLE_SELECTOR = "input:not([type='hidden']), select, textarea, button, [tabindex]:not([tabindex='-1'])";

const isVisible = (element: HTMLElement): boolean => {
  if (typeof element.checkVisibility === "function") {
    return element.checkVisibility({ checkOpacity: true, checkVisibilityCSS: true });
  }
  let current: HTMLElement | null = element;
  while (current) {
    const style = window.getComputedStyle(current);
    if (current.hidden || style.display === "none" || style.visibility === "hidden" || style.opacity === "0") {
      return false;
    }
    current = current.parentElement;
  }
  return true;
};

const isFullyInView = (element: HTMLElement): boolean => {
  const rect = element.getBoundingClientRect();
  if (
    rect.width <= 0 ||
    rect.height <= 0 ||
    rect.top < 0 ||
    rect.left < 0 ||
    rect.bottom > window.innerHeight ||
    rect.right > window.innerWidth
  ) {
    return false;
  }

  let ancestor = element.parentElement;
  while (ancestor) {
    const style = window.getComputedStyle(ancestor);
    const clipsX = /auto|scroll|hidden|clip/.test(style.overflowX || style.overflow);
    const clipsY = /auto|scroll|hidden|clip/.test(style.overflowY || style.overflow);
    if (clipsX || clipsY) {
      const bounds = ancestor.getBoundingClientRect();
      const top = bounds.top + ancestor.clientTop;
      const left = bounds.left + ancestor.clientLeft;
      if (
        (clipsY && (rect.top < top || rect.bottom > top + ancestor.clientHeight)) ||
        (clipsX && (rect.left < left || rect.right > left + ancestor.clientWidth))
      ) {
        return false;
      }
    }
    ancestor = ancestor.parentElement;
  }
  return true;
};

const findControl = (element: HTMLElement) =>
  Array.from(element.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).find(
    (control) => !control.matches(":disabled, [aria-disabled='true']") && isVisible(control),
  );

/**
 * Brings the first field with a validation error into view (inside a dialog or the page) and focuses its control.
 * Returns the target field, or null when the form has no visible errors.
 */
export const scrollToFirstFormError = (formElement: HTMLElement | null | undefined): HTMLElement | null => {
  const firstError = Array.from(formElement?.querySelectorAll<HTMLElement>(ERROR_SELECTOR) ?? []).find(isVisible);
  if (!formElement || !firstError) {
    return null;
  }

  // Walk up to the field wrapper that owns the error; stop at the form so we never target another field's control
  let fieldRoot = firstError.parentElement;
  let control: HTMLElement | undefined;
  while (fieldRoot && fieldRoot !== formElement) {
    control = findControl(fieldRoot);
    if (control) {
      break;
    }
    fieldRoot = fieldRoot.parentElement;
  }
  const field = fieldRoot && fieldRoot !== formElement ? fieldRoot : firstError;

  if (!isFullyInView(field)) {
    const prefersReducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    // "center" keeps the field clear of sticky headers/footers in both the page and dialogs
    field.scrollIntoView({ behavior: prefersReducedMotion ? "auto" : "smooth", block: "center" });
  }
  control?.focus({ preventScroll: true });

  return field;
};
